// src/components/SubmitTime/hooks/useTimeTracking.ts
// Custom hook for time tracking management
// Migrated from SubmitTime.js time tracking methods

import { useState, useCallback } from 'react';
import type { TimeEvent, TimeTrackingItem, ReplyItem } from '../types';

export interface TimeTrackingHelpers {
  timeTracking: TimeEvent[];
  setTimeTracking: (events: TimeEvent[]) => void;
  timeTrackingTeam: boolean;
  setTimeTrackingTeam: (value: boolean) => void;
  maxMinutesPerDay: number;
  setMaxMinutesPerDay: (value: number) => void;
  loadTimeTrackingEvents: (api: any, startDate: string, endDate: string) => Promise<TimeEvent[]>;
  buildTimeTrackingRequest: (params: {
    conversationId?: number;
    layerId: number;
    date: string;
    hours: string;
    projectId?: number;
    configValueId?: number;
    coreTaskId?: number;
    estimated?: string;
    remaining?: string;
    actual?: string;
    customFields?: any[];
    teamId?: number;
    replyId?: number;
    timeTrackingId?: number;
  }) => TimeTrackingItem;
  buildReplyRequest: (params: {
    conversationId: number;
    body: string;
    attachments?: any[];
    tagPeople?: number[];
    replyId?: number;
    socketConnectionId?: string;
  }) => ReplyItem;
  extractTagPeople: (body: string) => number[];
}

export function useTimeTracking(): TimeTrackingHelpers
{
  const [timeTracking, setTimeTracking] = useState<TimeEvent[]>([]);
  const [timeTrackingTeam, setTimeTrackingTeam] = useState<boolean>(false);
  const [maxMinutesPerDay, setMaxMinutesPerDay] = useState<number>(960); // 16 hours default

  // Load time tracking events for date range
  const loadTimeTrackingEvents = useCallback(
    async (_api: any, startDate: string, endDate: string): Promise<TimeEvent[]> =>
    {
      try
      {
        const { submitTimeService } = await import('@/services/submitTimeService');
        const events = await submitTimeService.getEvents({
          StartDate: startDate,
          EndDate: endDate,
        });
        setTimeTracking(events);
        return events;
      }
      catch (error)
      {
        console.error('Error loading time tracking events:', error);
        return [];
      }
    },
    [],
  );

  // Build time tracking request object
  const buildTimeTrackingRequest = useCallback(
    (params: {
      conversationId?: number;
      layerId: number;
      date: string;
      hours: string;
      projectId?: number;
      configValueId?: number;
      coreTaskId?: number;
      estimated?: string;
      remaining?: string;
      actual?: string;
      customFields?: any[];
      teamId?: number;
      replyId?: number;
      timeTrackingId?: number;
    }): TimeTrackingItem =>
    {
      return {
        ConversationId: params.conversationId,
        LayerId: params.layerId,
        Date: params.date,
        Hours: params.hours,
        ProjectID: params.projectId,
        ConfigValueID: params.configValueId,
        CoreTaskID: params.coreTaskId,
        Estimated: params.estimated,
        Remaining: params.remaining,
        Actual: params.actual,
        CustomFields: params.customFields,
        TeamId: params.teamId,
        ReplyId: params.replyId,
        TimeTrackingId: params.timeTrackingId,
      };
    },
    [],
  );

  // Build reply request object
  const buildReplyRequest = useCallback(
    (params: {
      conversationId: number;
      body: string;
      attachments?: any[];
      tagPeople?: number[];
      replyId?: number;
      socketConnectionId?: string;
    }): ReplyItem =>
    {
      return {
        ConversationId: params.conversationId,
        Body: params.body,
        Attachments: params.attachments,
        TagPeople: params.tagPeople,
        ReplyId: params.replyId,
        SocketConnectionId: params.socketConnectionId,
      };
    },
    [],
  );

  // Extract person IDs from body HTML (legacy tags)
  const extractTagPeople = useCallback((body: string): number[] =>
  {
    if (!body) return [];

    const tagPeople: number[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(body, 'text/html');

    // Find all spans with person-id attribute
    const personSpans = doc.querySelectorAll('span[person-id]');
    personSpans.forEach(span =>
    {
      const personId = span.getAttribute('person-id');
      if (personId)
      {
        const id = parseInt(personId);
        if (!isNaN(id) && !tagPeople.includes(id))
        {
          tagPeople.push(id);
        }
      }
    });

    return tagPeople;
  }, []);

  return {
    timeTracking,
    setTimeTracking,
    timeTrackingTeam,
    setTimeTrackingTeam,
    maxMinutesPerDay,
    setMaxMinutesPerDay,
    loadTimeTrackingEvents,
    buildTimeTrackingRequest,
    buildReplyRequest,
    extractTagPeople,
  };
}
